import { useEffect, useState } from "react";
import api from "../lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend } from "recharts";

const PALETTE = ["#FFB000", "#39FF14", "#61DAFB", "#FF6B6B", "#C77DFF", "#FFD93D", "#4ADE80"];

export default function Stats() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/computers/stats");
            setStats(data);
        })();
    }, []);

    if (!stats) {
        return (
            <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Compiling statistics<span className="terminal-caret" />
            </div>
        );
    }

    return (
        <div data-testid="stats-page" className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-10">
            <div className="border-b border-border/60 pb-8">
                <div className="overline">// Analytics</div>
                <h1 className="mt-3 font-serif text-6xl md:text-7xl">The Numbers</h1>
                <p className="mt-4 max-w-2xl font-sans text-muted-foreground">
                    A quantitative portrait of the collection: what was made, when, and by whom.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-10">
                <BigStat testid="pub-stat-total" label="Registered machines" value={stats.total} />
                <BigStat testid="pub-stat-value" label="Aggregate value (USD)" value={`$${stats.total_value.toLocaleString()}`} accent="green" />
                <BigStat testid="pub-stat-brands" label="Distinct manufacturers" value={stats.by_brand.length} />
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div data-testid="pub-chart-decade" className="border border-border bg-card p-6">
                    <div className="mb-6 font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                        &gt; DISTRIBUTION_BY_DECADE
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer>
                            <BarChart data={stats.by_decade}>
                                <XAxis dataKey="label" stroke="#666" />
                                <YAxis stroke="#666" allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 12 }}
                                    cursor={{ fill: "rgba(255,176,0,0.06)" }}
                                />
                                <Bar dataKey="count" fill="#FFB000">
                                    {stats.by_decade.map((_, i) => (
                                        <Cell key={i} fill="#FFB000" />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div data-testid="pub-chart-brand" className="border border-border bg-card p-6">
                    <div className="mb-6 font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                        &gt; SHARE_BY_MANUFACTURER
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={stats.by_brand}
                                    dataKey="count"
                                    nameKey="label"
                                    outerRadius={100}
                                    innerRadius={55}
                                    strokeWidth={1}
                                    stroke="#09090B"
                                >
                                    {stats.by_brand.map((_, i) => (
                                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                                    ))}
                                </Pie>
                                <Legend
                                    wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.15em" }}
                                />
                                <Tooltip
                                    contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 12 }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}

function BigStat({ label, value, accent, testid }) {
    const color = accent === "green" ? "text-phosphor-green" : "text-phosphor-amber";
    return (
        <div data-testid={testid} className="border border-border bg-card p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className={`mt-4 font-serif text-6xl ${color} glow-amber`}>{value}</div>
        </div>
    );
}
